import { Vector2 } from '../../../odie';
import { gsap } from 'gsap';

import type { StateObserver } from '../../../lib/pattern/StateMachine';
import { config } from '../../defs/config';
import type { BlockEntity } from '../../entities/BlockEntity';
import type { FallAction } from '../../util/MapFaller';
import { MapFaller } from '../../util/MapFaller';
import { mapGetPan, mapToViewPosition } from '../../util/mapTools';
import type { IPhase, PhaseSystem } from '../PhaseSystem';
import { app } from '../../app';

gsap.registerEase('square', (progress) => progress * progress);

export const getGravityDropTweenVars = (
  fromY: number,
  toY: number,
): gsap.TweenVars => {
  // solve duration for travel distance of square (gravity) ease
  const duration = Math.sqrt(toY - fromY) / config.sim.gravity;
  return { y: toY, duration, ease: 'power2.in' };
};

export const getGravityBounceTweenVars = (toY: number): gsap.TweenVars => ({
  y: toY - config.sim.bounce,
  duration: config.sim.bounceDuration,
  ease: 'power2.out',
  repeat: 1,
  yoyo: true,
});

/*
    handles game session fall phase
*/
export class FallPhase implements StateObserver, IPhase {
  // fields
  //-------------------------------------------------------------------------
  private readonly _system: PhaseSystem;
  private blocksPerColumn: Record<number, number> = {};

  // init
  //-------------------------------------------------------------------------
  constructor(system: PhaseSystem) {
    this._system = system;
  }

  // impl

  //-------------------------------------------------------------------------
  public async enter() {
    // do fall action
    await this._actionFall();
  }

  public step() {}

  // private: actions
  //-------------------------------------------------------------------------
  private async _actionFall(): Promise<void> {
    if (!this._system.scene) return;

    // Reset blocks per column count
    this.blocksPerColumn = {};

    const mapFaller = new MapFaller(this._system.scene);

    // build fall actions
    const fallActions = mapFaller.build();

    // if have fall actions
    if (fallActions.length > 0) {
      // animate all fall actions
      await Promise.all(
        fallActions.map((action) => this._actionAnimateFallAction(action)),
      );

      // enter active phase
      this._system.phase = 'active';
    } else {
      // enter shuffle phase
      this._system.phase = 'shuffle';
    }
  }

  private async _actionAnimateFallAction(request: FallAction): Promise<void> {
    const entity = request.entity;
    const position = entity.c.position.mapPosition;
    const fromPosition = new Vector2(position.x, request.fromRow);
    const toPosition = new Vector2(position.x, request.toRow);

    // get from and to view positions
    const fromViewPosition = mapToViewPosition(fromPosition);
    const toViewPosition = mapToViewPosition(toPosition);

    // animate to requested view position
    await this._animateFall(entity, fromViewPosition, toViewPosition);

    // set fall collision
    entity.c.block.collide('fall');
  }

  // private: animators
  //-------------------------------------------------------------------------
  private async _animateFall(
    blockEntity: BlockEntity,
    from: Vector2,
    to: Vector2,
  ): Promise<void> {
    const view = blockEntity.c.view2d.view;

    // set from position
    view.x = from.x;
    view.y = from.y;

    // Set blocks per column for sound effects
    const column = blockEntity.c.position.mapPosition.x;
    if (this.blocksPerColumn[column] === undefined) {
      this.blocksPerColumn[column] = 0;
    }

    // Only the first block in a column will play the fall sound effect.
    const firstBlock = this.blocksPerColumn[column] === 0;

    // Increase blocks per colmun count
    this.blocksPerColumn[column] += 1;

    // animate to requested view position
    await gsap
      .timeline()
      // gravity drop
      .to(view, getGravityDropTweenVars(from.y, to.y))
      .add(() => {
        // First impact sound. Full volume.
        if (firstBlock) {
          app.sound.play('puzzle-block-drop', {
            pan: mapGetPan(blockEntity.c.position.mapPosition),
            dupes: 1,
            dupesInterval: 200,
          });
        }
      })
      // bounce: inverse drop then drop
      .to(view, getGravityBounceTweenVars(to.y))
      .add(() => {
        this.blocksPerColumn[column] -= 1;
        // Second impact sound from bounce. Lower volume.
        if (firstBlock) {
          app.sound.play('puzzle-block-drop2', {
            pan: mapGetPan(blockEntity.c.position.mapPosition),
            volume: 0.2,
            dupes: 1,
            dupesInterval: 200,
          });
        }
      });
  }
}
